import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Edit01,
  RefreshCw01,
  Table,
  TextInput,
  Tooltip,
} from "@opendate-app/setlist";

import { toastSuccess, toastError } from "../../../../shared/toastHelper";
import { useMarketingSettingsContext } from "../MarketingSettingsIndexContainer";
import SmsSetupRequired from "./SmsSetupRequired";
import TwilioSubaccount from "./TwilioSubaccount";

const styles = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    alignItems: "end",
    gap: 16,
  },
  subheading: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  muted: {
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  syncRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  syncActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cell: {
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
};

const MessagingServiceStatus = ({ messagingService }) => {
  if (messagingService.status === "ready") {
    return <Badge color="success">Ready</Badge>;
  }

  if (messagingService.status === "registered") {
    return (
      <Tooltip
        title={messagingService.test_status === "pending" ?
          "Testing in progress..." :
          `Status: ${messagingService.test_status} - with error code: ${messagingService.test_error_code}`
        }
      >
        <span tabIndex={0}><Badge>Registered</Badge></span>
      </Tooltip>
    );
  }

  if (messagingService.status === "pending") {
    return (
      <Tooltip title="This Messaging Service has not been registered as A2P Compliant in the Twilio console">
        <span tabIndex={0}><Badge>Pending</Badge></span>
      </Tooltip>
    );
  }

  return <Badge>Disabled</Badge>;
};

const MESSAGING_SERVICE_COLUMNS = [
  { key: "name", header: "Messaging service", primary: true },
  {
    key: "phone_numbers",
    header: "Phone numbers",
    render: (messagingService) => (
      <span style={styles.cell}>{messagingService.twilio_phone_numbers.map((phoneNumber) => phoneNumber.phone_number).join(", ")}</span>
    ),
  },
  { key: "status", header: "Status", render: (messagingService) => <MessagingServiceStatus messagingService={messagingService} /> },
];

const CredentialsForm = ({ accountSid, setAccountSid, authToken, setAuthToken, isSaving, onSubmit, placeholders }) => (
  <form onSubmit={onSubmit} style={styles.form}>
    <TextInput
      label="Account SID"
      value={accountSid}
      disabled={isSaving}
      onChange={(e) => setAccountSid(e.target.value)}
      placeholder={placeholders ? "Enter your Twilio Account SID" : undefined}
    />
    <TextInput
      label="Auth Token"
      type="password"
      value={authToken}
      disabled={isSaving}
      onChange={(e) => setAuthToken(e.target.value)}
      placeholder={placeholders ? "Enter your Twilio Auth Token" : undefined}
    />
    <div>
      <Button type="submit" disabled={isSaving}>Test and Save</Button>
    </div>
  </form>
);

const TwilioAccountContainer = () => {
  const {
    csrfToken,
    team,
    venueOwnership,
    updateVenueOwnership,
    smsOnboardingEnabled,
  } = useMarketingSettingsContext();

  const venueTwilioAccount = venueOwnership.twilio_account;
  const isSubaccount = venueTwilioAccount?.type === "TwilioAccounts::Subaccount";
  const isManualAccount = !!venueTwilioAccount && !isSubaccount;

  const showManualCredentials = isManualAccount;
  const showManualCredentialsCreate = !venueTwilioAccount && !smsOnboardingEnabled;
  const showTwilioSubaccount = isSubaccount && venueOwnership.sms_status?.can_send;
  const showSmsSetupRequired = (!venueTwilioAccount && smsOnboardingEnabled) ||
    (isSubaccount && !venueOwnership.sms_status?.can_send);

  const [accountSid, setAccountSid] = useState(venueTwilioAccount?.account_sid || "");
  const [authToken, setAuthToken] = useState(venueTwilioAccount?.masked_auth_token || "");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    setAccountSid(venueTwilioAccount?.account_sid || "");
    setAuthToken(venueTwilioAccount?.masked_auth_token || "");
    setIsEditing(false);
  }, [venueOwnership.id]);

  const persistTwilioAccountState = (twilioAccount) => {
    updateVenueOwnership(venueOwnership.id, { twilio_account: twilioAccount });
  }

  const updateTwilioAccount = (e) => {
    e.preventDefault();
    setIsSaving(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/venue_ownerships/${venueOwnership.id}/twilio_account`, {
      twilio_account: {
        account_sid: accountSid,
        auth_token: authToken,
      },
    })
      .then(({ data }) => {
        persistTwilioAccountState(data);
        toastSuccess("Twilio account saved successfully");
      }).catch((error) => {
        toastError("Error saving Twilio account");
      })
      .finally(() => {
        setIsSaving(false);
        setIsEditing(false);
      });
  };

  const createTwilioAccount = (e) => {
    e.preventDefault();
    setIsSaving(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/venue_ownerships/${venueOwnership.id}/twilio_account`, {
      twilio_account: {
        account_sid: accountSid,
        auth_token: authToken,
      }
    })
      .then(({ data }) => {
        persistTwilioAccountState(data.twilioAccount);
        setAccountSid("");
        setAuthToken("");
        toastSuccess("Twilio account saved successfully");
      })
      .catch((error) => {
        toastError("Error saving Twilio account");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const syncSenderIds = (e) => {
    e.preventDefault();
    setIsSyncing(true);
    axios.get(`/teams/${team.id}/venue_ownerships/${venueOwnership.id}/twilio_account/sync_messaging_services`)
      .then(({ data }) => {
        persistTwilioAccountState(data);
        toastSuccess("Twilio messaging services synced successfully");
      })
      .catch((error) => {
        toastError("Error syncing messaging services");
      })
      .finally(() => {
        setIsSyncing(false);
      });
  };

  const startEditing = (e) => {
    e.preventDefault();
    setAccountSid(venueOwnership.twilio_account?.account_sid || "");
    setAuthToken(venueOwnership.twilio_account?.masked_auth_token || "");
    setIsEditing(true);
  };

  return (
    <Card
      header={(
        <CardHeader
          title="SMS"
          supportingText={showManualCredentials
            ? "Provide your Twilio account information in order to send SMS messages."
            : "Configure SMS messaging for your venues."}
        />
      )}
    >
      <div style={styles.body}>
        {showManualCredentialsCreate && (
          <>
            <div style={styles.subheading}>Twilio Account Settings</div>
            <CredentialsForm
              accountSid={accountSid}
              setAccountSid={setAccountSid}
              authToken={authToken}
              setAuthToken={setAuthToken}
              isSaving={isSaving}
              onSubmit={createTwilioAccount}
              placeholders
            />
          </>
        )}
        {showManualCredentials && (
          <>
            <div style={styles.subheading}>Twilio Account Settings</div>
            {isEditing ? (
              <CredentialsForm
                accountSid={accountSid}
                setAccountSid={setAccountSid}
                authToken={authToken}
                setAuthToken={setAuthToken}
                isSaving={isSaving}
                onSubmit={updateTwilioAccount}
              />
            ) : (
              <div style={styles.form}>
                <TextInput label="Account SID" disabled type="text" value={venueTwilioAccount.account_sid || ""} />
                <TextInput label="Auth Token" disabled type="text" value={venueTwilioAccount.masked_auth_token || ""} />
                <div>
                  <Button icon={Edit01} disabled={isSaving} onClick={startEditing}>Edit</Button>
                </div>
              </div>
            )}
            <div style={styles.syncRow}>
              <div>
                <div style={styles.subheading}>Synced A2P Messaging Services</div>
                <div style={styles.muted}>Message Services & Phone numbers can be synced from your Twilio account for use as From Addresses.</div>
              </div>
              <div style={styles.syncActions}>
                <div style={styles.muted}>Last synced {venueTwilioAccount.human_last_synced_at}</div>
                <Button icon={RefreshCw01} disabled={isSyncing} onClick={syncSenderIds}>Sync With Twilio</Button>
              </div>
            </div>
            <Table
              columns={MESSAGING_SERVICE_COLUMNS}
              data={venueTwilioAccount.twilio_messaging_services || []}
              getRowKey={(messagingService) => messagingService.id}
              loading={isSyncing}
              loadingLabel="Syncing"
              empty={<div style={styles.muted}>No messaging services synced yet.</div>}
              stackBelow={640}
            />
          </>
        )}
        {showTwilioSubaccount && <TwilioSubaccount />}
        {showSmsSetupRequired && <SmsSetupRequired />}
      </div>
    </Card>
  );
};

export default TwilioAccountContainer;
